import config from '../config';

const CURRENT_VEHICLE_RENDER_VERSION = 2;
const PRODUCTION_DASHBOARD_HOST = 'dashboard.gophr.app';
const PRODUCTION_FILES_HOST = 'https://api-files.gophr.app';

const isProductionDashboardBrowser = () =>
    typeof window !== 'undefined' &&
    window.location &&
    typeof window.location.hostname === 'string' &&
    (window.location.hostname === PRODUCTION_DASHBOARD_HOST || window.location.hostname.endsWith(`.${PRODUCTION_DASHBOARD_HOST}`));

const resolveProductionBrowserHost = (configuredHost, productionHost) => {
    if (!isProductionDashboardBrowser()) {
        return configuredHost;
    }

    if (typeof configuredHost === 'string' && configuredHost.includes('dev-api-')) {
        return productionHost;
    }

    return configuredHost || productionHost;
};

const normalizeApiFilesHost = hostValue => {
    const host = String(hostValue || '').trim();

    if (!host) {
        return '';
    }

    if (typeof window !== 'undefined' && window.location && window.location.protocol === 'https:' && host.startsWith('http://')) {
        const insecureHost = host.slice('http://'.length);
        const isLocalInsecureHost = insecureHost.startsWith('localhost') || insecureHost.startsWith('127.0.0.1');

        if (!isLocalInsecureHost) {
            return `https://${insecureHost}`;
        }
    }

    return host;
};

const getApiFilesHost = () => {
    const configuredHost = config && config.config && config.config.api_files && config.config.api_files.host
        ? String(config.config.api_files.host).trim()
        : '';

    return normalizeApiFilesHost(resolveProductionBrowserHost(configuredHost, PRODUCTION_FILES_HOST));
};

const getDocumentAssetCandidates = document => {
    if (!document) {
        return [];
    }

    const candidates = [];

    // Prefer stable asset locations; signed URLs can expire and should be fallback only.
    if (typeof document.url === 'string' && document.url.trim()) {
        candidates.push(document.url.trim());
    }

    if (typeof document.path_id === 'string' && document.path_id.trim()) {
        const normalizedPathId = document.path_id.trim();
        candidates.push(normalizedPathId.startsWith('/') ? normalizedPathId : `/local-files/${normalizedPathId}`);
    }

    if (typeof document.signed_url === 'string' && document.signed_url.trim()) {
        candidates.push(document.signed_url.trim());
    }

    return candidates.filter((candidate, index, allCandidates) => allCandidates.indexOf(candidate) === index);
};

const getDocumentTimestamp = value => {
    if (value === undefined || value === null || value === '') {
        return Number.NEGATIVE_INFINITY;
    }

    const numericValue = Number(value);
    if (Number.isFinite(numericValue)) {
        return numericValue;
    }

    const parsedValue = Date.parse(String(value));
    return Number.isFinite(parsedValue) ? parsedValue : Number.NEGATIVE_INFINITY;
};

export const isSignedAssetUrl = url => {
    if (!url || !/^https?:\/\//.test(url)) {
        return false;
    }

    return [
        'X-Amz-Algorithm=',
        'X-Amz-Signature=',
        'X-Amz-Credential=',
        'X-Goog-Algorithm=',
        'X-Goog-Signature=',
        'X-Goog-Credential=',
        'AWSAccessKeyId=',
        'Signature=',
    ].some(marker => url.includes(marker));
};

const stripDisplayTimestamp = url => {
    if (!url) {
        return url;
    }

    try {
        const parsedUrl = new URL(url);
        parsedUrl.searchParams.delete('display_ts');
        return parsedUrl.toString();
    } catch (error) {
        void error;
        return url.replace(/([?&])display_ts=[^&]*(&|$)/, (match, prefix, suffix) => {
            if (prefix === '?' && suffix === '&') {
                return '?';
            }

            return prefix === '&' ? '' : '';
        }).replace(/[?&]$/, '');
    }
};

const buildDisplayAssetUrl = (url, version) => {
    const normalizedUrl = normalizeAssetUrl(url);

    if (!normalizedUrl || !/^https?:\/\//.test(normalizedUrl) || !Number.isFinite(version)) {
        return normalizedUrl;
    }

    if (isSignedAssetUrl(normalizedUrl)) {
        return stripDisplayTimestamp(normalizedUrl);
    }

    const separator = normalizedUrl.includes('?') ? '&' : '?';
    return `${normalizedUrl}${separator}display_ts=${Math.trunc(version)}`;
};

const getDocumentDisplayVersion = document => {
    return Math.max(
        getDocumentTimestamp(document && document.updated_at),
        getDocumentTimestamp(document && document.created_at),
        getDocumentTimestamp(document && document.ocr_text && document.ocr_text.render_version),
    );
};

export const normalizeAssetUrl = candidate => {
    if (typeof candidate !== 'string') {
        return '';
    }

    const trimmedCandidate = candidate.trim();

    if (!trimmedCandidate) {
        return '';
    }

    if (trimmedCandidate.startsWith('//')) {
        return `https:${trimmedCandidate}`;
    }

    const apiFilesHost = getApiFilesHost();

    const isPrivateIpv4Host = hostname => /^(10\.|127\.|192\.168\.|172\.(1[6-9]|2\d|3[0-1])\.)/.test(hostname);
    const isInternalAssetHostname = hostname => {
        if (!hostname) {
            return false;
        }

        const normalizedHostname = String(hostname).trim().toLowerCase();

        if (!normalizedHostname) {
            return false;
        }

        if (
            normalizedHostname === 'localhost' ||
            normalizedHostname === '0.0.0.0' ||
            isPrivateIpv4Host(normalizedHostname)
        ) {
            return true;
        }

        if (
            normalizedHostname.endsWith('.local') ||
            normalizedHostname.endsWith('.internal') ||
            normalizedHostname.endsWith('.cluster.local')
        ) {
            return true;
        }

        if (/(api[-_]?files|file[-_]?service|files[-_]?api)/i.test(normalizedHostname)) {
            return true;
        }

        return !normalizedHostname.includes('.');
    };

    const stripEmbeddedHostPrefix = value => {
        if (typeof value !== 'string') {
            return '';
        }

        const normalizedValue = value.trim().replace(/^\/+/, '');

        if (!normalizedValue) {
            return '';
        }

        const slashIndex = normalizedValue.indexOf('/');

        if (slashIndex <= 0) {
            return normalizedValue;
        }

        const firstSegment = normalizedValue.slice(0, slashIndex);
        const looksLikeHostSegment =
            firstSegment.includes('.') ||
            firstSegment.includes(':') ||
            firstSegment.toLowerCase() === 'localhost' ||
            firstSegment.toLowerCase() === '0.0.0.0' ||
            isPrivateIpv4Host(firstSegment);

        if (!looksLikeHostSegment) {
            return normalizedValue;
        }

        return normalizedValue.slice(slashIndex + 1);
    };

    if (!/^[a-z][a-z\d+.-]*:/i.test(trimmedCandidate) && apiFilesHost) {
        const strippedCandidate = stripEmbeddedHostPrefix(trimmedCandidate);
        return `${apiFilesHost.replace(/\/$/, '')}/${strippedCandidate}`;
    }

    if (apiFilesHost && /^https?:\/\//.test(trimmedCandidate)) {
        try {
            const candidateUrl = new URL(trimmedCandidate);
            const apiFilesUrl = new URL(apiFilesHost);
            const isLoopbackHost = candidateUrl.hostname === 'localhost' || candidateUrl.hostname === '127.0.0.1';

            if (isLoopbackHost || isInternalAssetHostname(candidateUrl.hostname)) {
                candidateUrl.protocol = apiFilesUrl.protocol;
                candidateUrl.hostname = apiFilesUrl.hostname;
                candidateUrl.port = apiFilesUrl.port;
                return candidateUrl.toString();
            }
        } catch (error) {
            void error;
        }
    }

    return trimmedCandidate;
};

export const getVehicleIdentity = vehicle => {
    if (!vehicle) {
        return null;
    }

    if (vehicle.vehicle_id) {
        return String(vehicle.vehicle_id);
    }

    if (vehicle.id !== undefined && vehicle.id !== null) {
        return String(vehicle.id);
    }

    return null;
};

const RENDER_SOURCE_ALLOWLIST = new Set([
    'ai_vehicle_render',
    'cached_ai_vehicle_render',
    'shared_vehicle_image_asset'
]);

const REGISTRATION_HINT_PATTERN = /(registration|insurance|license|plate|document)/i;
const RENDER_HINT_PATTERN = /(vehicle[-_ ]?render|ai[-_ ]?vehicle|shared[-_ ]?vehicle)/i;

const getAssetHintText = (...candidates) =>
    candidates
        .filter(candidate => typeof candidate === 'string' && candidate.trim())
        .join(' ')
        .trim();

const isLikelyDriverAppVehicleImageAsset = url => {
    const normalizedUrl = normalizeAssetUrl(url);

    if (!normalizedUrl) {
        return false;
    }

    if (/\/local-files\//i.test(normalizedUrl)) {
        return true;
    }

    return /api-files\.gophr\.app/i.test(normalizedUrl);
};

const isLikelyVehicleRenderAsset = ({ url = '', fileName = '', source = '' } = {}) => {
    const normalizedUrl = normalizeAssetUrl(url);
    const normalizedSource = typeof source === 'string' ? source.trim().toLowerCase() : '';
    const hintText = getAssetHintText(normalizedUrl, fileName, normalizedSource);

    if (!normalizedUrl) {
        return false;
    }

    if (normalizedSource === 'vehicle_type_stencil') {
        return false;
    }

    if (RENDER_SOURCE_ALLOWLIST.has(normalizedSource)) {
        return true;
    }

    if (REGISTRATION_HINT_PATTERN.test(hintText)) {
        return false;
    }

    return RENDER_HINT_PATTERN.test(hintText);
};

const getVehicleRenderDocuments = vehicle => {
    if (!vehicle || !Array.isArray(vehicle.VehicleDocuments)) {
        return [];
    }

    return vehicle.VehicleDocuments.filter(document => document && document.doc_type === 'vehicle_render');
};

const getVehicleRenderDocumentSharedAssetId = document => {
    if (!document || document.doc_type !== 'vehicle_render') {
        return '';
    }

    const source = document && document.ocr_text && document.ocr_text.source ? document.ocr_text.source : null;

    if (source === 'vehicle_type_stencil') {
        return '';
    }

    const sharedAssetId = document && document.ocr_text && document.ocr_text.shared_asset_id
        ? String(document.ocr_text.shared_asset_id).trim()
        : '';

    return sharedAssetId;
};

const hasNonStencilVehicleRenderDocument = vehicle => {
    return getVehicleRenderDocuments(vehicle).some(document => {
        const source = document && document.ocr_text && document.ocr_text.source ? document.ocr_text.source : null;
        return source !== 'vehicle_type_stencil' && getDocumentAssetCandidates(document).some(candidate => Boolean(normalizeAssetUrl(candidate)));
    });
};

const getVehicleDocumentRenderUrls = vehicle => {
    const renderDocuments = getVehicleRenderDocuments(vehicle)
        .filter(document => {
            if (!document || document.doc_type !== 'vehicle_render') {
                return false;
            }

            if (document && document.ocr_text && document.ocr_text.source === 'vehicle_type_stencil') {
                return false;
            }

            return getDocumentAssetCandidates(document).some(candidate => Boolean(normalizeAssetUrl(candidate)));
        })
        .slice()
        .sort((left, right) => {
            const leftTime = new Date(left && left.updated_at ? left.updated_at : 0).getTime();
            const rightTime = new Date(right && right.updated_at ? right.updated_at : 0).getTime();
            return rightTime - leftTime;
        });

    const currentRenderDocuments = renderDocuments.filter(document => Number(document && document.ocr_text && document.ocr_text.render_version ? document.ocr_text.render_version : 0) >= CURRENT_VEHICLE_RENDER_VERSION);
    const preferredDocuments = currentRenderDocuments.length > 0 ? currentRenderDocuments : renderDocuments;

    return preferredDocuments
        .flatMap(document => getDocumentAssetCandidates(document).map(candidate => buildDisplayAssetUrl(candidate, getDocumentDisplayVersion(document))))
        .filter(Boolean);
};

export const getVehicleRenderSharedAssetIds = vehicle => {
    const renderDocuments = getVehicleRenderDocuments(vehicle)
        .filter(document => Boolean(getVehicleRenderDocumentSharedAssetId(document)))
        .slice()
        .sort((left, right) => {
            const leftTime = new Date(left && left.updated_at ? left.updated_at : 0).getTime();
            const rightTime = new Date(right && right.updated_at ? right.updated_at : 0).getTime();
            return rightTime - leftTime;
        });

    const currentRenderDocuments = renderDocuments.filter(document => Number(document && document.ocr_text && document.ocr_text.render_version ? document.ocr_text.render_version : 0) >= CURRENT_VEHICLE_RENDER_VERSION);
    const preferredDocuments = currentRenderDocuments.length > 0 ? currentRenderDocuments : renderDocuments;

    return preferredDocuments.reduce((sharedAssetIds, document) => {
        const sharedAssetId = getVehicleRenderDocumentSharedAssetId(document);

        if (sharedAssetId && !sharedAssetIds.includes(sharedAssetId)) {
            sharedAssetIds.push(sharedAssetId);
        }

        return sharedAssetIds;
    }, []);
};

const getVehicleFieldRenderUrls = vehicle => {
    if (!vehicle) {
        return [];
    }

    const explicitRenderUrl = normalizeAssetUrl(vehicle.render_image_url);
    const hasVehicleRenderDocs = getVehicleRenderDocuments(vehicle).length > 0;
    const candidates = [];

    if (explicitRenderUrl && (!hasVehicleRenderDocs || hasNonStencilVehicleRenderDocument(vehicle))) {
        candidates.push(explicitRenderUrl);
    }

    [vehicle.image_url, vehicle.vehicle_image_url].forEach(candidate => {
        const normalizedCandidate = normalizeAssetUrl(candidate);

        if (!normalizedCandidate) {
            return;
        }

        // Some production AI render URLs are opaque signed links without clear
        // filename hints; keep strict filtering for plain URLs.
        if ((isLikelyVehicleRenderAsset({ url: normalizedCandidate }) || isSignedAssetUrl(normalizedCandidate)) && !candidates.includes(normalizedCandidate)) {
            candidates.push(normalizedCandidate);
        }
    });

    return candidates;
};

const dedupeAssetUrls = candidates => {
    return (Array.isArray(candidates) ? candidates : []).reduce((urls, candidate) => {
        const normalizedCandidate = normalizeAssetUrl(candidate);

        if (normalizedCandidate && !urls.includes(normalizedCandidate)) {
            urls.push(normalizedCandidate);
        }

        return urls;
    }, []);
};

export const getVehicleRenderCandidates = vehicle => {
    return dedupeAssetUrls([
        ...getVehicleDocumentRenderUrls(vehicle),
        ...getVehicleFieldRenderUrls(vehicle),
    ]);
};

export const getVehicleRenderUrl = vehicle => {
    if (!vehicle) {
        return '';
    }

    return getVehicleRenderCandidates(vehicle)[0] || '';
};

export const resolveDriverVehicle = (driver, preferredVehicleId = null) => {
    if (!driver) {
        return null;
    }

    const directVehicles = Array.isArray(driver.Vehicles) ? driver.Vehicles : [];
    const userVehicles = Array.isArray(driver.User && driver.User.Vehicles) ? driver.User.Vehicles : [];
    const actionDriverVehicles = Array.isArray(driver.LastDriverActions && driver.LastDriverActions.Driver && driver.LastDriverActions.Driver.Vehicles)
        ? driver.LastDriverActions.Driver.Vehicles
        : [];
    const candidateVehicles = [...directVehicles, ...userVehicles, ...actionDriverVehicles];
    const normalizedPreferredVehicleId = preferredVehicleId ? String(preferredVehicleId) : null;

    if (normalizedPreferredVehicleId) {
        const preferredVehicle = candidateVehicles.find(vehicle => getVehicleIdentity(vehicle) === normalizedPreferredVehicleId);

        if (preferredVehicle) {
            return preferredVehicle;
        }

        if (driver.active_vehicle_details && getVehicleIdentity(driver.active_vehicle_details) === normalizedPreferredVehicleId) {
            return driver.active_vehicle_details;
        }
    }

    return null;
};

export const resolveDriverActiveVehicle = driver => {
    if (!driver) {
        return null;
    }

    const actionDriver = driver.LastDriverActions && driver.LastDriverActions.Driver ? driver.LastDriverActions.Driver : null;
    const activeVehicleId = driver.active_vehicle || (driver.User && driver.User.active_vehicle) || (actionDriver && actionDriver.active_vehicle) || null;
    const activeVehicleDetails = driver.active_vehicle_details || (actionDriver && actionDriver.active_vehicle_details) || null;
    const activeVehicleIdentity = activeVehicleId ? String(activeVehicleId) : null;

    if (!activeVehicleIdentity && activeVehicleDetails) {
        return activeVehicleDetails;
    }

    const matchedVehicle = resolveDriverVehicle(driver, activeVehicleIdentity);

    if (matchedVehicle) {
        return matchedVehicle;
    }

    const directVehicles = Array.isArray(driver.Vehicles) ? driver.Vehicles : [];
    const userVehicles = Array.isArray(driver.User && driver.User.Vehicles) ? driver.User.Vehicles : [];
    const actionDriverVehicles = Array.isArray(actionDriver && actionDriver.Vehicles) ? actionDriver.Vehicles : [];

    return activeVehicleDetails || directVehicles[0] || userVehicles[0] || actionDriverVehicles[0] || driver.vehicle_blob || (driver.User && driver.User.vehicle_blob) || null;
};

export const getDriverVehicleRenderUrl = driver => {
    return getDriverVehicleRenderCandidates(driver)[0] || '';
};

const getDriverDirectImageCandidates = driver => {
    if (!driver) {
        return [];
    }

    const directCandidates = [
        driver.render_image_url,
        driver.image_url,
        driver.vehicle_image_url,
        driver.active_vehicle_details && driver.active_vehicle_details.render_image_url,
        driver.active_vehicle_details && driver.active_vehicle_details.image_url,
        driver.active_vehicle_details && driver.active_vehicle_details.vehicle_image_url,
        driver.vehicle_blob && driver.vehicle_blob.render_image_url,
        driver.vehicle_blob && driver.vehicle_blob.image_url,
        driver.vehicle_blob && driver.vehicle_blob.vehicle_image_url,
        driver.User && driver.User.render_image_url,
        driver.User && driver.User.image_url,
        driver.User && driver.User.vehicle_image_url,
        driver.User && driver.User.vehicle_blob && driver.User.vehicle_blob.render_image_url,
        driver.User && driver.User.vehicle_blob && driver.User.vehicle_blob.image_url,
        driver.User && driver.User.vehicle_blob && driver.User.vehicle_blob.vehicle_image_url,
        driver.Driver && driver.Driver.render_image_url,
        driver.Driver && driver.Driver.image_url,
        driver.Driver && driver.Driver.vehicle_image_url,
        driver.Driver && driver.Driver.vehicle_blob && driver.Driver.vehicle_blob.render_image_url,
        driver.Driver && driver.Driver.vehicle_blob && driver.Driver.vehicle_blob.image_url,
        driver.Driver && driver.Driver.vehicle_blob && driver.Driver.vehicle_blob.vehicle_image_url,
        driver.driver_blob && driver.driver_blob.render_image_url,
        driver.driver_blob && driver.driver_blob.image_url,
        driver.driver_blob && driver.driver_blob.vehicle_image_url,
        driver.driver_blob && driver.driver_blob.vehicle_blob && driver.driver_blob.vehicle_blob.render_image_url,
        driver.driver_blob && driver.driver_blob.vehicle_blob && driver.driver_blob.vehicle_blob.image_url,
        driver.driver_blob && driver.driver_blob.vehicle_blob && driver.driver_blob.vehicle_blob.vehicle_image_url,
    ];

    return dedupeAssetUrls(directCandidates);
};

export const getDriverVehicleDisplayCandidates = (driver, supplementalDrivers = []) => {
    const driverContexts = [driver, ...(Array.isArray(supplementalDrivers) ? supplementalDrivers : [supplementalDrivers])]
        .filter(Boolean);

    return dedupeAssetUrls([
        ...driverContexts.flatMap(candidateDriver => getDriverVehicleRenderCandidates(candidateDriver)),
        ...driverContexts.flatMap(candidateDriver => getDriverDirectImageCandidates(candidateDriver)),
    ]);
};

export const getDriverVehicleDisplayUrl = (driver, supplementalDrivers = []) => {
    return getDriverVehicleDisplayCandidates(driver, supplementalDrivers)[0] || '';
};

export const getDriverVehicleRenderCandidates = driver => {
    if (!driver) {
        return [];
    }

    const candidateVehicles = [
        resolveDriverActiveVehicle(driver),
        driver.vehicle_blob,
        driver.User && driver.User.vehicle_blob,
    ].filter(Boolean);

    const fallbackCandidates = [driver.render_image_url, driver.vehicle_image_url, driver.image_url]
        .map(candidate => {
            const normalizedCandidate = normalizeAssetUrl(candidate);
            return (
                isLikelyVehicleRenderAsset({ url: normalizedCandidate }) ||
                isSignedAssetUrl(normalizedCandidate) ||
                isLikelyDriverAppVehicleImageAsset(normalizedCandidate)
            )
                ? normalizedCandidate
                : '';
        })
        .filter(Boolean);

    return dedupeAssetUrls([
        ...candidateVehicles.flatMap(vehicle => getVehicleRenderCandidates(vehicle)),
        ...fallbackCandidates,
    ]);
};